import React, { useEffect, useMemo, useState } from 'react';
import { Icon, Message, Modal } from 'semantic-ui-react';
import styled from 'styled-components';

import LoadingSpinner from 'common/components/LoadingSpinner';
import PositionedModal from 'common/components/PositionedModal';
import { PageSection } from 'common/styles/page';

import { useGetPulseSurveyAnalyticsQuery } from '../api/pulseSurveyPortalApi';

const SCORE_LABEL: Record<number, string> = {
  1: 'Strongly Disagree',
  2: 'Disagree',
  3: 'Neutral',
  4: 'Agree',
  5: 'Strongly Agree',
};

const SCORE_SHORT_LABEL: Record<number, string> = {
  1: 'Strongly Disagree',
  2: 'Disagree',
  3: 'Neutral',
  4: 'Agree',
  5: 'Strongly Agree',
};

const SCORE_COLOR: Record<number, string> = {
  1: '#e06666',
  2: '#f6ad55',
  3: '#f6e05e',
  4: '#68d391',
  5: '#38b2ac',
};

const MULTIPLE_CHOICE_BAR_COLOR = '#3b82f6';

type PulseSurveyLikertAnalyticsProps = {
  surveyId: number;
  validId: boolean;
  organizationName?: string | null;
  selectedParticipantTeamId: number | null;
  onSelectedParticipantTeamIdChange: (value: number | null) => void;
  onParticipantTeamOptionsChange: (options: Array<{ key: string; value: string; text: string; content: React.ReactNode; disabled?: boolean }>) => void;
};

const FEEDBACK_MODAL_HEIGHT = 'min(540px, 80vh)';
const FALLBACK_THEME_LABEL = 'Organization';
const FALLBACK_PILLAR_LABEL = 'Unassigned';

const averageScore = (counts: Array<{ score: number; count: number }>) => {
  const total = counts.reduce((sum, row) => sum + row.count, 0);
  if (total <= 0) return 0;
  const weighted = counts.reduce((sum, row) => sum + row.score * row.count, 0);
  return weighted / total;
};

const scoreToPercent = (score: number) => {
  if (score <= 1) return 0;
  if (score >= 5) return 100;
  return ((score - 1) / 4) * 100;
};

const formatSentimentPercent = (percent: number) => `${Math.round(percent)}%`;

const isLikertQuestion = (question: { question_type?: 'likert' | 'multiple_choice' | 'multi_select' }) =>
  (question.question_type ?? 'likert') === 'likert';

const isMultiSelectQuestion = (question: { question_type?: 'likert' | 'multiple_choice' | 'multi_select' }) =>
  question.question_type === 'multi_select';

const multiSelectCaptionText = (maxSelections?: number | null) =>
  Number.isInteger(maxSelections) && Number(maxSelections) > 0
    ? `Percentage of respondents who selected each option. Respondents could select up to ${maxSelections}.`
    : 'Percentage of respondents who selected each option. Respondents could select all that apply.';

const PulseSurveyLikertAnalytics: React.FC<PulseSurveyLikertAnalyticsProps> = ({
  surveyId,
  validId,
  organizationName,
  selectedParticipantTeamId,
  onSelectedParticipantTeamIdChange,
  onParticipantTeamOptionsChange,
}) => {
  const [activeFeedbackQuestionId, setActiveFeedbackQuestionId] = useState<number | null>(null);

  const { data, isLoading, error } = useGetPulseSurveyAnalyticsQuery(
    {
      id: surveyId,
      participant_team_id: selectedParticipantTeamId ?? undefined,
    },
    {
      skip: !validId,
    }
  );

  const analyticsOrganizationName = data?.organization_name || organizationName || 'organization';
  const participantTeamOptions = useMemo(() => {
    const participantTeams = [...(data?.participant_teams ?? [])];

    participantTeams.sort((a, b) => {
      const aEligible = a.response_count >= 3;
      const bEligible = b.response_count >= 3;
      if (aEligible !== bEligible) {
        return aEligible ? -1 : 1;
      }
      return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
    });

    return [
      {
        key: 'all',
        value: 'all',
        text: 'All teams',
        content: <TeamFilterOptionName>All teams</TeamFilterOptionName>,
      },
      ...participantTeams.map((participantTeam) => ({
        key: `participant-team-${participantTeam.id}`,
        value: String(participantTeam.id),
        text: participantTeam.name,
        content: (
          <TeamFilterOptionContent>
            <TeamFilterOptionName>{participantTeam.name}</TeamFilterOptionName>
            <TeamFilterOptionMeta>
              {participantTeam.response_count} response{participantTeam.response_count === 1 ? '' : 's'}
            </TeamFilterOptionMeta>
          </TeamFilterOptionContent>
        ),
        disabled: participantTeam.response_count < 3,
      })),
    ];
  }, [data?.participant_teams, analyticsOrganizationName]);

  useEffect(() => {
    if (!data || selectedParticipantTeamId === null) {
      return;
    }
    const exists = data.participant_teams.some(
      (participantTeam) => participantTeam.id === selectedParticipantTeamId && participantTeam.response_count >= 3
    );
    if (!exists) {
      onSelectedParticipantTeamIdChange(null);
    }
  }, [data, selectedParticipantTeamId, onSelectedParticipantTeamIdChange]);

  useEffect(() => {
    setActiveFeedbackQuestionId(null);
  }, [selectedParticipantTeamId]);

  useEffect(() => {
    onParticipantTeamOptionsChange(participantTeamOptions);
  }, [participantTeamOptions, onParticipantTeamOptionsChange]);

  const questions = data?.questions ?? [];
  const activeFeedbackQuestion = useMemo(
    () => questions.find((question) => question.question_id === activeFeedbackQuestionId) ?? null,
    [questions, activeFeedbackQuestionId]
  );
  const activeFeedbackQuestionIsLikert = activeFeedbackQuestion
    ? isLikertQuestion(activeFeedbackQuestion)
    : true;
  const activeFeedbackModalGroups = useMemo(() => {
    if (!activeFeedbackQuestion) {
      return [] as Array<{ score: number; label?: string; feedbacks: string[] }>;
    }

    const feedbackGroups = activeFeedbackQuestion.feedback_groups ?? [];

    if (!isMultiSelectQuestion(activeFeedbackQuestion)) {
      return feedbackGroups;
    }

    // Multi-select free-text feedback is attached to the whole response, not a single option.
    // Flatten and de-duplicate so the modal avoids implying feedback belongs to one option.
    const seen = new Set<string>();
    const mergedFeedbacks: string[] = [];

    feedbackGroups.forEach((group) => {
      group.feedbacks.forEach((feedback) => {
        const normalized = feedback.trim();
        if (!normalized || seen.has(normalized)) {
          return;
        }
        seen.add(normalized);
        mergedFeedbacks.push(feedback);
      });
    });

    if (!mergedFeedbacks.length) {
      return [];
    }

    return [
      {
        score: 0,
        label: 'Feedback responses',
        feedbacks: mergedFeedbacks,
      },
    ];
  }, [activeFeedbackQuestion]);
  const questionMetrics = useMemo(
    () =>
      questions.map((question) => {
        const likertCounts = question.counts ?? [];
        const avgScore = isLikertQuestion(question) ? averageScore(likertCounts) : 0;
        const strengthPercent = isLikertQuestion(question) ? scoreToPercent(avgScore) : null;
        return {
          ...question,
          avgScore,
          strengthPercent,
        };
      }),
    [questions]
  );
  const themeGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        key: string;
        label: string;
        order: number;
        questions: typeof questionMetrics;
      }
    >();

    questionMetrics.forEach((question) => {
      const themeId = typeof question.theme === 'number' ? question.theme : null;
      const key = themeId === null ? 'theme-unassigned' : `theme-${themeId}`;
      const label = (question.theme_label || '').trim() || (themeId === null ? FALLBACK_THEME_LABEL : `Theme ${themeId}`);
      const order = themeId === null ? Number.MAX_SAFE_INTEGER : themeId;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label,
          order,
          questions: [],
        });
      }

      groups.get(key)?.questions.push(question);
    });

    return [...groups.values()].sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.label.localeCompare(b.label, 'en', { sensitivity: 'base' });
    });
  }, [questionMetrics]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <PageSection padtop>
        <Message negative data-testid="pulse-analytics-error" content="Failed to load analytics for this pulse survey." />
      </PageSection>
    );
  }

  if (!questions.length) {
    return (
      <PageSection padtop>
        <Message info data-testid="pulse-analytics-empty">
          <Message.Header>No responses yet</Message.Header>
          <p>Analytics will appear here after members submit this pulse survey.</p>
        </Message>
      </PageSection>
    );
  }

  return (
    <PageSection padtop data-testid="pulse-analytics-tab">
      <QuestionCards>
        <QuestionCard>
          {themeGroups.map((themeGroup) => (
            <ThemeSection key={themeGroup.key}>
              <ThemeHeader>{themeGroup.label}</ThemeHeader>
              <ThemeQuestions>
                {themeGroup.questions.map((question) => {
                  const hasFeedback = Boolean(question.feedback_groups && question.feedback_groups.length > 0);
                  const feedbackCount = question.feedback_groups?.reduce((sum, group) => sum + group.feedbacks.length, 0) ?? 0;
                  const feedbackLabel = `${feedbackCount} feedback response${feedbackCount === 1 ? '' : 's'}`;
                  const pillarLabel = (question.pillar_label || '').trim();
                  const showPillar = pillarLabel.length > 0;
                  const likertQuestion = isLikertQuestion(question);
                  const multiSelectQuestion = isMultiSelectQuestion(question);
                  const optionCounts = question.option_counts ?? [];

                  return (
                    <QuestionItem key={question.question_id}>
                      <DistributionRow $stacked={!likertQuestion}>
                        {likertQuestion ? (
                          <>
                            <DistributionMeta>
                              <QuestionHeaderRow>
                                <QuestionText>{question.question}</QuestionText>
                              </QuestionHeaderRow>
                              {showPillar && (
                                <PillarMetaRow>
                                  <Icon name="tags" aria-hidden="true" className="pillar-icon" />
                                  <span>{pillarLabel}</span>
                                </PillarMetaRow>
                              )}
                              <ResponseMetaRow>
                                <ResponseTotal>
                                  <Icon name="users" aria-hidden="true" className="responses-icon" />
                                  {question.total_responses} response{question.total_responses === 1 ? '' : 's'}
                                </ResponseTotal>
                                <FeedbackButton
                                  $active={hasFeedback}
                                  type="button"
                                  onClick={() => setActiveFeedbackQuestionId(question.question_id)}
                                  disabled={!hasFeedback}
                                  aria-label={`View feedback for ${question.question}`}
                                  title={hasFeedback ? feedbackLabel : 'No feedback responses yet'}
                                >
                                  <Icon
                                    name={hasFeedback ? 'comment alternate' : 'comment outline'}
                                    color={hasFeedback ? 'teal' : 'grey'}
                                    aria-hidden="true"
                                    className="feedback-icon"
                                  />
                                  <span>{hasFeedback ? feedbackLabel : 'No feedback'}</span>
                                </FeedbackButton>
                              </ResponseMetaRow>
                            </DistributionMeta>

                            <DistributionVisuals>
                              <BarsButton
                                type="button"
                                onClick={() => {
                                  if (hasFeedback) {
                                    setActiveFeedbackQuestionId(question.question_id);
                                  }
                                }}
                                disabled={!hasFeedback}
                                aria-label={`View feedback for ${question.question}`}
                                title={hasFeedback ? feedbackLabel : 'No feedback responses yet'}
                              >
                                <Bars data-testid="pulse-analytics-distribution">
                                  {(question.counts ?? []).map((scoreCount) => {
                                    const percent =
                                      question.total_responses > 0
                                        ? (scoreCount.count / question.total_responses) * 100
                                        : 0;
                                    const displayPercent = `${Math.round(percent)}%`;

                                    return (
                                      <MiniBarColumn key={scoreCount.score}>
                                        <MiniBarPercent>{displayPercent}</MiniBarPercent>
                                        <MiniBarTile>
                                          <MiniBarFill
                                            style={{
                                              height: `${Math.max(0, Math.min(100, percent))}%`,
                                              backgroundColor: SCORE_COLOR[scoreCount.score] ?? '#9ca3af',
                                            }}
                                          />
                                        </MiniBarTile>
                                        <MiniBarLabel>{SCORE_SHORT_LABEL[scoreCount.score] ?? SCORE_LABEL[scoreCount.score]}</MiniBarLabel>
                                      </MiniBarColumn>
                                    );
                                  })}
                                </Bars>
                              </BarsButton>

                              <DistributionSentimentSummary>
                                <strong>{formatSentimentPercent(question.strengthPercent ?? 0)}</strong>
                                <span>sentiment<br />strength</span>
                              </DistributionSentimentSummary>
                            </DistributionVisuals>
                          </>
                        ) : (
                          <MultipleChoiceDistributionWrap>
                            <MultipleChoiceTopRow>
                              <MultipleChoiceQuestionBlock>
                                <QuestionHeaderRow>
                                  <QuestionText>{question.question}</QuestionText>
                                </QuestionHeaderRow>
                                {showPillar && (
                                  <PillarMetaRow>
                                    <Icon name="tags" aria-hidden="true" className="pillar-icon" />
                                    <span>{pillarLabel}</span>
                                  </PillarMetaRow>
                                )}
                                <MultipleChoiceResponseMetaRow>
                                  <ResponseTotal>
                                    <Icon name="users" aria-hidden="true" className="responses-icon" />
                                    {question.total_responses} response{question.total_responses === 1 ? '' : 's'}
                                  </ResponseTotal>
                                  <FeedbackButton
                                    $active={hasFeedback}
                                    type="button"
                                    onClick={() => setActiveFeedbackQuestionId(question.question_id)}
                                    disabled={!hasFeedback}
                                    aria-label={`View feedback for ${question.question}`}
                                    title={hasFeedback ? feedbackLabel : 'No feedback responses yet'}
                                  >
                                    <Icon
                                      name={hasFeedback ? 'comment alternate' : 'comment outline'}
                                      color={hasFeedback ? 'teal' : 'grey'}
                                      aria-hidden="true"
                                      className="feedback-icon"
                                    />
                                    <span>{hasFeedback ? feedbackLabel : 'No feedback'}</span>
                                  </FeedbackButton>
                                </MultipleChoiceResponseMetaRow>
                              </MultipleChoiceQuestionBlock>
                            </MultipleChoiceTopRow>
                            <DistributionVisuals $stacked>
                              <MultipleChoiceBarsButton
                                type="button"
                                onClick={() => {
                                  if (hasFeedback) {
                                    setActiveFeedbackQuestionId(question.question_id);
                                  }
                                }}
                                disabled={!hasFeedback}
                                aria-label={`View feedback for ${question.question}`}
                                title={hasFeedback ? feedbackLabel : 'No feedback responses yet'}
                              >
                                <MultipleChoiceBars data-testid="pulse-analytics-multiple-choice-distribution">
                                  {optionCounts.map((optionCount) => {
                                    const percent =
                                      question.total_responses > 0
                                        ? (optionCount.count / question.total_responses) * 100
                                        : 0;
                                    const normalizedWidth = Math.max(0, Math.min(100, percent));

                                    return (
                                      <MultipleChoiceOptionItem key={`${question.question_id}-${optionCount.value}`}>
                                        <MultipleChoiceOptionLabel>{optionCount.option}</MultipleChoiceOptionLabel>
                                        <MultipleChoiceBarTrack>
                                          <MultipleChoiceBarFill
                                            style={{
                                              width: `${normalizedWidth}%`,
                                              backgroundColor: MULTIPLE_CHOICE_BAR_COLOR,
                                            }}
                                          />
                                        </MultipleChoiceBarTrack>
                                        <MultipleChoiceBarMeta>
                                          {Math.round(percent)}% ({optionCount.count})
                                        </MultipleChoiceBarMeta>
                                      </MultipleChoiceOptionItem>
                                    );
                                  })}
                                </MultipleChoiceBars>
                                {multiSelectQuestion && (
                                  <MultipleChoiceCaption>
                                    {multiSelectCaptionText(question.max_selections)}
                                  </MultipleChoiceCaption>
                                )}
                              </MultipleChoiceBarsButton>
                            </DistributionVisuals>
                          </MultipleChoiceDistributionWrap>
                        )}
                      </DistributionRow>
                    </QuestionItem>
                  );
                })}
              </ThemeQuestions>
            </ThemeSection>
          ))}
        </QuestionCard>
      </QuestionCards>

      <PositionedModal
        open={Boolean(activeFeedbackQuestion)}
        onClose={() => setActiveFeedbackQuestionId(null)}
        size="small"
        closeIcon
        style={{ height: FEEDBACK_MODAL_HEIGHT, display: 'flex', flexDirection: 'column' }}
      >
        <Modal.Header>{activeFeedbackQuestion?.question ?? 'Feedback Responses'}</Modal.Header>
        <ScrollableModalContent>
          {!activeFeedbackModalGroups.length ? (
            <EmptyFeedback>No feedback responses for this question yet.</EmptyFeedback>
          ) : (
            <FeedbackGroupList>
              {activeFeedbackModalGroups.map((group, groupIndex) => (
                <FeedbackGroup key={`${group.score}-${groupIndex}`}>
                  <FeedbackGroupHeader>
                    <FeedbackGroupHeaderLeft>
                      <ScoreBullet
                        style={{
                          backgroundColor: activeFeedbackQuestionIsLikert
                            ? SCORE_COLOR[group.score] ?? '#9ca3af'
                            : MULTIPLE_CHOICE_BAR_COLOR,
                        }}
                      />
                      <span>{group.label || SCORE_LABEL[group.score] || `Option ${group.score}`}</span>
                    </FeedbackGroupHeaderLeft>
                    <FeedbackGroupResponseCount>
                      {group.feedbacks.length} response{group.feedbacks.length === 1 ? '' : 's'}
                    </FeedbackGroupResponseCount>
                  </FeedbackGroupHeader>
                  <FeedbackItems>
                    {group.feedbacks.map((feedback, idx) => (
                      <FeedbackItem key={`${group.score}-${idx}`}>
                        <Icon name="comment alternate" aria-hidden="true" className="feedback-entry-icon" />
                        <span>{feedback}</span>
                      </FeedbackItem>
                    ))}
                  </FeedbackItems>
                </FeedbackGroup>
              ))}
            </FeedbackGroupList>
          )}
        </ScrollableModalContent>
      </PositionedModal>
    </PageSection>
  );
};

const QuestionCards = styled.div`
  display: grid;
  gap: 10px;
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 12px 14px;
`;

const TeamFilterOptionContent = styled.div`
  display: grid;
  text-align: left;
  line-height: 1.25;
`;

const TeamFilterOptionName = styled.div`
  font-weight: 600;
  color: #111827;
`;

const TeamFilterOptionMeta = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
`;

const QuestionCard = styled.div`
  display: grid;
  width: 100%;
  align-content: start;
  padding: 0 1px 2px;
  gap: 12px;
`;

const ThemeSection = styled.section`
  display: grid;
  align-content: start;
  gap: 10px;
  border-top: 1px solid #d1d5db;
  padding-top: 12px;
`;

const ThemeHeader = styled.h4`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #2f3d4d;
  font-weight: 400;
`;

const ThemeQuestions = styled.div`
  display: grid;
  gap: 18px;
`;

const QuestionItem = styled.div`
  padding: 14px 0;
`;

const DistributionRow = styled.div<{ $stacked?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $stacked }) => ($stacked ? 'minmax(0, 1fr)' : 'minmax(470px, 1.55fr) minmax(500px, 720px)')};
  align-items: center;
  gap: ${({ $stacked }) => ($stacked ? '12px' : '10px')};

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const DistributionMeta = styled.div`
  display: grid;
  align-content: start;
  gap: 10px;
  min-width: 0;
  padding-left: 0;
  align-self: center;
`;

const DistributionVisuals = styled.div<{ $stacked?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: ${({ $stacked }) => ($stacked ? 'flex-start' : 'flex-end')};
  gap: 12px;
  width: 100%;

  @media (max-width: 980px) {
    justify-content: flex-start;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

const QuestionHeaderRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
  align-items: start;
  gap: 0;
`;

const ResponseMetaRow = styled.div`
  display: grid;
  align-items: start;
  justify-items: start;
  gap: 10px;
  padding-left: 6px;
  margin-top: 0;
`;

const PillarMetaRow = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: #5b6678;
  display: inline-grid;
  grid-template-columns: 14px auto;
  align-items: center;
  justify-content: start;
  gap: 6px;
  line-height: 1.2;
  padding-left: 6px;
  margin-top: 0;

  .pillar-icon {
    margin: 0 !important;
    width: 14px;
    text-align: center;
    color: #7a8798;
    font-size: 0.9rem !important;
    line-height: 1 !important;
  }
`;

const FeedbackButton = styled.button<{ $active?: boolean }>`
  border: 1px solid transparent;
  background: #ffffff;
  color: ${({ $active }) => ($active ? '#5b6678' : '#8a95a6')};
  display: inline-grid;
  grid-template-columns: 14px auto;
  align-items: center;
  justify-content: start;
  gap: 6px;
  cursor: pointer;
  padding: 0 6px;
  margin-left: -6px;
  border-radius: 6px;
  transition: color 120ms ease, background-color 120ms ease, border-color 120ms ease;

  span {
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
  }

  &:hover:not(:disabled) {
    color: #2563eb;
    background: #eff6ff;
    border-color: #93c5fd;
  }

  .feedback-icon {
    margin: 0 !important;
    width: 14px;
    text-align: center;
    font-size: 0.9rem !important;
    line-height: 1 !important;
  }

  &:disabled {
    cursor: not-allowed;
    color: #5b6678;
    opacity: 1;
  }
`;

const QuestionText = styled.h3<{ $centered?: boolean }>`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #0f172a;
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
  width: 100%;
  max-width: ${({ $centered }) => ($centered ? '360px' : 'none')};
  margin-left: ${({ $centered }) => ($centered ? 'auto' : '0')};
  margin-right: ${({ $centered }) => ($centered ? 'auto' : '0')};
  line-height: 1.4;
  min-height: 0;
`;

const ResponseTotal = styled.p<{ $centered?: boolean }>`
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: #5b6678;
  display: inline-grid;
  grid-template-columns: 14px auto;
  align-items: center;
  justify-content: start;
  gap: 6px;
  line-height: 1.2;
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
  width: ${({ $centered }) => ($centered ? '100%' : 'auto')};
  max-width: ${({ $centered }) => ($centered ? '360px' : 'none')};
  margin-left: ${({ $centered }) => ($centered ? 'auto' : '0')};
  margin-right: ${({ $centered }) => ($centered ? 'auto' : '0')};
  padding-bottom: 0;

  .responses-icon {
    margin: 0 !important;
    width: 14px;
    text-align: center;
    color: #7a8798;
    font-size: 0.9rem !important;
    line-height: 1 !important;
  }
`;

const BarsButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  border-radius: 8px;

  &:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
  }
`;

const Bars = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 64px);
  gap: 6px;
  align-items: end;
  justify-items: center;
  justify-content: center;
  width: auto;

  @media (max-width: 980px) {
    width: auto;
  }
`;

const MultipleChoiceDistributionWrap = styled.div`
  width: 100%;
  min-width: 0;

  @media (max-width: 980px) {
    width: 100%;
  }
`;

const MultipleChoiceTopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: 10px;
  margin-bottom: 26px;
  padding-right: 0;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const MultipleChoiceQuestionBlock = styled.div`
  display: grid;
  align-content: start;
  gap: 8px;
  min-width: 0;
`;

const MultipleChoiceResponseMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding-left: 6px;
  padding-top: 3px;
  margin-bottom: 4px;

  ${ResponseTotal} {
    justify-content: start;
    text-align: left;
    width: auto;
  }

  ${FeedbackButton} {
    margin-left: 0;
    margin-right: 0;
  }

  @media (max-width: 980px) {
    flex-wrap: wrap;
    gap: 10px;
    padding-left: 4px;
  }
`;

const MultipleChoiceBars = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;
  max-width: none;
  margin-left: auto;
  padding-right: 4px;

  @media (max-width: 980px) {
    width: 100%;
    max-width: none;
    margin-left: 0;
    padding-right: 0;
  }
`;

const MultipleChoiceBarsButton = styled(BarsButton)`
  display: block;
  width: 100%;
`;

const MultipleChoiceCaption = styled.p`
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #64748b;
`;

const MultipleChoiceOptionItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(280px, 42vw, 620px) 84px;
  grid-template-areas: 'label bar meta';
  align-items: center;
  gap: 16px;
  width: 100%;

  @media (max-width: 980px) {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'label meta'
      'bar bar';
    gap: 10px;
  }
`;

const MultipleChoiceOptionLabel = styled.span`
  grid-area: label;
  font-size: 13px;
  color: #334155;
  line-height: 1.3;
  font-weight: 500;
  text-align: left;
  min-width: 0;
  padding-left: 6px;

  @media (max-width: 980px) {
    text-align: left;
    padding-left: 4px;
  }
`;

const MultipleChoiceBarTrack = styled.div`
  grid-area: bar;
  width: 100%;
  justify-self: end;
  height: 18px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
  border: 1px solid #d1d5db;
`;

const MultipleChoiceBarFill = styled.div`
  height: 100%;
  min-width: 0;
  border-radius: 999px;
  transition: width 220ms ease-out;
`;

const MultipleChoiceBarMeta = styled.span`
  grid-area: meta;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  justify-self: end;
  min-width: 84px;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  white-space: nowrap;
  text-align: right;

  @media (max-width: 980px) {
    min-width: 0;
  }
`;

const DistributionSentimentSummary = styled.div`
  width: 132px;
  min-height: 128px;
  display: grid;
  justify-items: center;
  align-content: center;
  gap: 4px;

  strong {
    font-size: 27px;
    line-height: 1;
    color: #0f172a;
  }

  span {
    font-size: 12px;
    font-weight: 400;
    color: #4f5d6d;
    text-transform: lowercase;
    text-align: center;
  }
`;

const MiniBarColumn = styled.div`
  display: grid;
  width: 64px;
  grid-template-rows: 14px 96px 28px;
  justify-items: center;
  align-content: start;
  gap: 4px;
  min-width: 0;
  min-height: 146px;
`;

const MiniBarPercent = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1;
`;

const MiniBarTile = styled.div`
  width: 64px;
  height: 96px;
  border-radius: 8px;
  background: #e5e7eb;
  padding: 3px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
`;

const MiniBarFill = styled.div`
  width: 100%;
  border-radius: 5px;
  min-height: 0;
  transition: height 220ms ease-out;
`;

const MiniBarLabel = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: #4f5d6d;
  width: 64px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.2;
  white-space: normal;
  overflow-wrap: break-word;
`;

const ScrollableModalContent = styled(Modal.Content)`
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
`;

const EmptyFeedback = styled.p`
  margin: 0;
  color: #6b7280;
`;

const FeedbackGroupList = styled.div`
  display: grid;
  gap: 14px;
`;

const FeedbackGroup = styled.div`
  display: grid;
  gap: 8px;
  border-top: 1px solid #e5e7eb;
  padding-top: 10px;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;

const FeedbackGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: 'Poppins', sans-serif;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;

const FeedbackGroupHeaderLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const FeedbackGroupResponseCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.2;
  text-align: right;
  white-space: nowrap;
`;

const ScoreBullet = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: 0 0 auto;
  margin-top: 4px;
`;

const FeedbackItems = styled.ul`
  margin: 0;
  margin-top: 5px;
  padding-left: 18px;
  list-style: none;
  display: grid;
  gap: 7px;
`;

const FeedbackItem = styled.li`
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr);
  align-items: start;
  gap: 8px;
  color: #374151;
  line-height: 1.35;
  font-size: 13px;
  font-family: 'Poppins', sans-serif;

  .feedback-entry-icon {
    margin: 0 !important;
    width: 14px;
    font-size: 0.85rem !important;
    line-height: 1.2 !important;
    color: #9ca3af !important;
    text-align: center;
  }
`;


export default PulseSurveyLikertAnalytics;
